import { redirect } from '@tanstack/react-router';

import type { CodebaseTab } from '@/hooks/useCodebaseRoute';
import type { FileRouteTypes } from '@/routeTree.gen';

/**
 * A fully-typed TanStack Router link descriptor for codebase breadcrumb segments.
 * Using `to: FileRouteTypes['to']` ensures only registered routes are expressible,
 * catching typos like a literal `$` at compile time.
 */
export interface CodebaseBreadcrumbLink {
  to: FileRouteTypes['to'];
  params: Record<string, string>;
  search?: Record<string, string>;
}

/**
 * Appends branchId and versionId as query params to a base path.
 * Returns the base path unchanged when neither value is provided.
 */
export function buildVersionedUrl(
  basePath: string,
  { branchId, versionId }: { branchId?: string | null; versionId?: string | null }
): string {
  const params = new URLSearchParams();
  if (branchId) params.set('branchId', branchId);
  if (versionId) params.set('versionId', versionId);
  const qs = params.toString();
  return qs ? `${basePath}?${qs}` : basePath;
}

/** Typed TanStack Router link descriptor for a codebase breadcrumb segment. */
export function buildCodebaseBreadcrumbLink({
  branchId,
  codebaseId,
  organizationName,
  pathSegments,
  tab,
  versionId,
}: {
  branchId?: string | null;
  codebaseId: string;
  organizationName: string;
  pathSegments: string;
  tab: CodebaseTab | undefined;
  versionId?: string | null;
}): CodebaseBreadcrumbLink {
  const baseParams = { organizationName, codebaseId };
  const searchEntries: Record<string, string> = {};
  if (branchId) searchEntries.branchId = branchId;
  if (versionId) searchEntries.versionId = versionId;
  const search = Object.keys(searchEntries).length > 0 ? searchEntries : undefined;

  switch (tab) {
    case 'overview':
      return { to: '/$organizationName/cb/$codebaseId/overview/$', params: { ...baseParams, _splat: '' }, search };
    case 'branches':
      return { to: '/$organizationName/cb/$codebaseId/branches', params: baseParams, search };
    case 'autodocs':
      return { to: '/$organizationName/cb/$codebaseId/autodocs', params: baseParams, search };
    case 'atlas':
      return { to: '/$organizationName/cb/$codebaseId/atlas', params: baseParams, search };
    case 'code':
      return {
        to: '/$organizationName/cb/$codebaseId/code/$',
        params: { ...baseParams, _splat: pathSegments },
        search,
      };
    case 'settings':
      return { to: '/$organizationName/cb/$codebaseId/settings/general', params: baseParams, search };
    case 'tree':
    default:
      return {
        to: '/$organizationName/cb/$codebaseId/tree/$',
        params: { ...baseParams, _splat: pathSegments },
        search,
      };
  }
}

type CodebaseRouteParams = { codebaseId: string; organizationName: string };
type CodebaseRouteSearch = { branch?: string; branchCodebase?: string; branchId?: string; versionId?: string };

/**
 * Link options for the codebase Settings tab, carrying the branch/version
 * selection and any open branch sheet along. Shared by the retired Overview
 * route's `beforeLoad` redirect and the flag gates on the Docs, Autodocs, and
 * Atlas tabs, so every path into Settings keeps the same state.
 */
export function codebaseSettingsLinkOptions(
  { codebaseId, organizationName }: CodebaseRouteParams,
  { branch, branchCodebase, branchId, versionId }: CodebaseRouteSearch
) {
  return {
    to: '/$organizationName/cb/$codebaseId/settings/general' as const,
    params: { organizationName, codebaseId },
    search: { branchId, versionId, branch, branchCodebase },
  };
}

/**
 * Redirect for the retired Overview tab: its URLs land on the Settings tab so
 * bookmarks and in-app links keep working. Throw the result from a route's
 * `beforeLoad`.
 */
export function redirectToCodebaseSettings(params: CodebaseRouteParams, search: CodebaseRouteSearch) {
  return redirect(codebaseSettingsLinkOptions(params, search));
}
